import React, { useState, useRef, useEffect } from "react";
import { IconCalendarWeek } from "@tabler/icons-react";

type DateRange = {
  startDate: string;
  endDate: string;
};

interface DateRangeInputProps {
  title?: string;
  name: string;
  disabled?: boolean;
  isEditable?: boolean;
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  register?: any;
  error?: any;
  validation?: any;
  inputClassName?: string;
  className?: string;
  labelClass?: string;
}

const DateRangeInput: React.FC<DateRangeInputProps> = ({
  title,
  name,
  disabled,
  isEditable = true,
  value,
  onChange,
  placeholder = "YYYY-MM-DD - YYYY-MM-DD",
  register,
  error,
  validation,
  inputClassName = "",
  className = "",
  labelClass = "",
}) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(value?.startDate || "");
  const [endDate, setEndDate] = useState(value?.endDate || "");

  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync with parent
  useEffect(() => {
    if (value) {
      setStartDate(value.startDate);
      setEndDate(value.endDate);
    }
  }, [value]);

  const formattedValue =
    startDate && endDate ? `${startDate} - ${endDate}` : "";

  const handleApply = () => {
    onChange?.({ startDate, endDate });
    setOpen(false);
  };

  return (
    <div className={`space-y-1 w-full relative ${className}`} ref={ref}>
      {title && (
        <label
          htmlFor={name}
          className={
            labelClass ? labelClass : "text-[#1E293B] text-xs whitespace-nowrap"
          }
        >
          {title}
          {validation?.required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}

      {/* Fake Input (matches your Input UI) */}
      <div
        onClick={() => isEditable && !disabled && setOpen(!open)}
        className={`flex items-center rounded-full px-2 py-2 text-xs w-full outline-none ${isEditable && !disabled
            ? "bg-white border border-[#D8D8D8] cursor-pointer"
            : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
          } ${inputClassName}`}
      >
        <input
          id={name}
          name={name}
          readOnly
          {...(register ? register(name, validation) : {})}
          value={formattedValue}
          placeholder={placeholder}
          disabled={disabled}
          className="bg-transparent flex-1 text-xs placeholder:text-[#212121] focus:outline-none cursor-pointer"
        />
        <IconCalendarWeek size={16} stroke={1.5} />
      </div>

      {/* Validation error */}
      {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}

      {/* Dropdown */}
      {open && isEditable && !disabled && (
        <div className="absolute mt-1 bg-white border border-gray-200 shadow-lg rounded-md p-4 z-50 w-72">
          <div className="flex flex-col gap-2">
            <div>
              <label className="text-xs text-gray-500">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs"
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs"
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <button
              onClick={handleApply}
              className="mt-2 w-full bg-[#212121] text-[#fff] rounded-md py-2 text-xs"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeInput;
